import { infiniteQueryOptions } from '@tanstack/react-query';
import type {
  HevitonApiV2,
  RawOrderType,
  RawPageNode,
  RawPlantListResponse,
  RawPlantNode,
  RawPlantOrderNode,
  RawPlantSubscriptionNode,
  RawSubscriptionCode,
  RawSubscriptionStatus,
} from '@heviton/heviton-api-v2';
import { fromRawSubscriptionList } from '@/shared/data-mappers';
import type { SubscriptionListItem } from '@/shared/data-types';

/**
 * 발전소 목록 + 구독 상태 query 정의 SSOT. BE = v2 `GET /plants?include=subscription` (미구독 포함).
 * 서버 페이지네이션(page/size, Spring PageImpl) → `useInfiniteQuery` + 하단 [더보기] 로 페이지 누적.
 *
 * mock/real 전환은 `USE_MOCK` flag 로 제어 — true 면 mock 을 page/size 로 잘라 매퍼에 통과,
 * false 면 실제 `api.plant.list` 호출(타입·키 구조 동일). BE 미연동/미커버 케이스를 덮기 위한 임시 장치.
 *
 * 결정/미결: `docs/subscription-list-decisions.md`.
 */
export const SUBSCRIPTIONS_QUERY_KEY = ['subscriptions'] as const;

/** mock 토글 — true=mock, false=실제 v2 `plant.list`. 실연동 완료 시 flag·mock 함께 제거. [TODO:mock] */
const USE_MOCK = false;

/** mock 비움 토글 — true 면 발전소 0건(빈 상태 `EmptyState` 화면 확인용). USE_MOCK=true 일 때만 의미. [TODO:mock] */
const MOCK_EMPTY = false;

/** 페이지 크기 — BE 기본값(10)과 정합. */
const PAGE_SIZE = 10;

// --- mock helpers (RawPlantNode/SubscriptionNode 필수 필드 채움) ---

function plant(
  over: Pick<RawPlantNode, 'plantId' | 'plantName' | 'location' | 'volume'> & Partial<RawPlantNode>,
): RawPlantNode {
  return {
    customerId: 'cust-001',
    isPublic: false,
    isRepresentative: false,
    companyId: 1,
    energy: '501',
    processStatus: '',
    checkStatus: '',
    facilitiesStatus: '',
    ...over,
  };
}

function sub(
  status: RawSubscriptionStatus,
  code: RawSubscriptionCode,
  startDate: string,
  endDate: string,
): RawPlantSubscriptionNode {
  return {
    plantSubscriptionId: 1,
    subscriptionId: 1,
    subscriptionNo: 'SUB-20260101-MOCK',
    subscriptionCode: code,
    subscriptionStatus: status,
    currentPeriodStartDate: startDate,
    currentPeriodEndDate: endDate,
    // 약정기간 — raw 필수필드라 채우되 FE 미사용(currentPeriod 만 표기, decisions §3). 값은 현재 주기와 동일.
    contractStartDate: startDate,
    contractEndDate: endDate,
  };
}

// 발전소 주문 노드 — `include=order`. type=BILLING 이 결제변경 게이팅 기준.
function order(type: RawOrderType, name: string, amount: number): RawPlantOrderNode {
  return {
    orderId: 1,
    type,
    recurring: type === 'BILLING',
    name,
    status: 'REQUESTED',
    amount,
    productId: 1,
    createTime: '2026-06-22T04:24:00Z',
    updateTime: '2026-06-23T09:17:36Z',
  };
}

// mock — 모든 케이스 coverage: 상태(active/inactive/canceled/expired) × 유형(mon/pre5y/pub1y/trial) + 미구독.
const MOCK_RESPONSE: RawPlantListResponse = {
  plants: [
    // 구독중 + 월구독 → "구독중" + [결제 변경]
    plant({
      plantId: 'plant-001',
      plantName: '서울식물원주차장태양광발전소',
      location: '서울시 강서구',
      volume: 500,
      subscription: sub('ACTIVE', 'MON', '2026-01-01T00:00:00Z', '2026-01-31T00:00:00Z'),
      order: order('BILLING', '월 구독', 37400),
    }),
    // 구독중 + 5년 일시납 → "구독중", 결제변경 없음
    plant({
      plantId: 'plant-002',
      plantName: '헤리트태양광발전소01',
      location: '경기도 성남시 분당구',
      volume: 300,
      subscription: sub('ACTIVE', 'PRE5Y', '2024-03-01T00:00:00Z', '2029-02-28T00:00:00Z'),
    }),
    // 미구독(과거 이력) + 월구독 → [구독하기]
    plant({
      plantId: 'plant-003',
      plantName: '헤리트태양광발전소02',
      location: '경기도 성남시 분당구',
      volume: 200,
      subscription: sub('INACTIVE', 'MON', '2026-06-01T00:00:00Z', '2026-06-30T00:00:00Z'),
      order: order('BILLING', '월 구독', 37400),
    }),
    // 미구독(과거 이력) + 일시납 → [구독하기]
    plant({
      plantId: 'plant-004',
      plantName: '강남옥상태양광발전소',
      location: '서울시 강남구',
      volume: 150,
      subscription: sub('INACTIVE', 'PRE5Y', '2025-01-01T00:00:00Z', '2029-12-31T00:00:00Z'),
    }),
    // 구독중 + 월구독 → "구독중" + [결제 변경]
    plant({
      plantId: 'plant-005',
      plantName: '판교제2태양광발전소',
      location: '경기도 성남시 분당구',
      volume: 1500,
      subscription: sub('ACTIVE', 'MON', '2026-07-01T00:00:00Z', '2026-07-31T00:00:00Z'),
      order: order('BILLING', '월 구독', 37400),
    }),
    // 공공 1년 구독중
    plant({
      plantId: 'plant-006',
      plantName: '세종행정타운태양광발전소',
      location: '세종시',
      volume: 450,
      subscription: sub('ACTIVE', 'PUB1Y', '2026-01-01T00:00:00Z', '2026-12-31T00:00:00Z'),
    }),
    // 체험 구독중
    plant({
      plantId: 'plant-007',
      plantName: '대전유성구청사옥상태양광',
      location: '대전시 유성구',
      volume: 95,
      subscription: sub('ACTIVE', 'TRIAL', '2026-06-01T00:00:00Z', '2026-06-14T00:00:00Z'),
    }),
    // 해지 → 재구독 대상([구독하기])
    plant({
      plantId: 'plant-008',
      plantName: '광교호수공원태양광발전소',
      location: '경기도 수원시 영통구',
      volume: 250,
      subscription: sub('CANCELED', 'MON', '2025-01-01T00:00:00Z', '2025-06-30T00:00:00Z'),
    }),
    // 만료 → 재구독 대상([구독하기])
    plant({
      plantId: 'plant-009',
      plantName: '동탄제3태양광발전소',
      location: '경기도 화성시',
      volume: 320,
      subscription: sub('EXPIRED', 'PRE5Y', '2019-01-01T00:00:00Z', '2024-12-31T00:00:00Z'),
    }),
    // 미구독 (subscription 없음) → [구독하기]
    plant({
      plantId: 'plant-010',
      plantName: '청주산업단지태양광',
      location: '충북 청주시',
      volume: 800,
    }),
    // 미구독 (subscriptionCode NONE) → [구독하기]
    plant({
      plantId: 'plant-011',
      plantName: '여수산업단지태양광',
      location: '전남 여수시',
      volume: 1200,
      subscription: sub('CANCELED', 'NONE', '', ''),
    }),
  ],
};

/** infinite query 의 페이지 단위 — 매핑된 행 + 다음 페이지 판단용 pageInfo. */
export interface SubscriptionsPage {
  items: SubscriptionListItem[];
  pageInfo?: RawPageNode;
}

/** mock 페이지 — 전체 mock 발전소를 page/size 로 잘라 pageInfo 와 함께 BE 응답을 모사. */
function mockPage(page: number): RawPlantListResponse {
  const all = MOCK_EMPTY ? [] : MOCK_RESPONSE.plants;
  const start = page * PAGE_SIZE;
  return {
    plants: all.slice(start, start + PAGE_SIZE),
    pageInfo: {
      page, // 요청·응답 pageInfo.page 모두 0-base
      size: PAGE_SIZE,
      totalElements: all.length,
      totalPages: Math.ceil(all.length / PAGE_SIZE),
    },
  };
}

export const subscriptionsQueryOptions = (api: HevitonApiV2) =>
  infiniteQueryOptions({
    queryKey: SUBSCRIPTIONS_QUERY_KEY,
    initialPageParam: 0,
    queryFn: async ({ pageParam, signal }): Promise<SubscriptionsPage> => {
      if (USE_MOCK) {
        await new Promise((resolve) => setTimeout(resolve, 300)); // mock latency
        const raw = mockPage(pageParam);
        return { items: fromRawSubscriptionList(raw), pageInfo: raw.pageInfo };
      }
      const raw = await api.plant.list(
        { include: 'subscription,order', page: pageParam, size: PAGE_SIZE },
        { signal },
      );
      return { items: fromRawSubscriptionList(raw), pageInfo: raw.pageInfo };
    },
    // 요청·응답 page 모두 0-base. echo 를 신뢰하지 않고 누적 개수로 다음 페이지 판단.
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.pageInfo?.totalElements ?? 0;
      const loaded = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < total ? allPages.length : undefined;
    },
  });
